import { createSignal, onMount, Show, For, splitProps, mergeProps, createMemo, children as resolveChildren } from 'solid-js';
import { useLicense } from '@util/context';

export default function Editor(props) {
	const merged = mergeProps(
		{
			control: 'Control',
			style: 'Style',
			advanced: 'Advanced',
			default: 'control',
		},
		props
	);

	const [local, others] = splitProps(merged, [
		'control',
		'style',
		'advanced',
		'default',
		'children',
		'controlContent',
		'styleContent',
		'advancedContent',
		'class',
	]);

	const { isLocked } = useLicense();
	const [tab, setTab] = createSignal(local.default);

	// Parse children for slot content
	const resolved = resolveChildren(() => local.children);

	const getSlotContent = (slotName) => {
		const kids = resolved();
		if (!kids) return null;
		const arr = Array.isArray(kids) ? kids : [kids];
		return arr.find(child => child?.slot === slotName || child?.props?.slot === slotName) || null;
	};

	const controlContent = createMemo(() => local.controlContent || getSlotContent('control'));
	const styleContent = createMemo(() => local.styleContent || getSlotContent('style'));
	const advancedContent = createMemo(() => local.advancedContent || getSlotContent('advanced'));

	const tabs = createMemo(() => {
		const result = {};
		if (controlContent()) result.control = local.control;
		if (styleContent()) result.style = local.style;
		if (advancedContent()) result.advanced = local.advanced;
		return result;
	});

	const tabKeys = createMemo(() => Object.keys(tabs()));
	const showTabs = createMemo(() => tabKeys().length > 1);

	onMount(() => {
		const keys = tabKeys();
		if (keys.length > 0 && !keys.includes(tab())) {
			setTab(keys[0]);
		}
	});

	const tabIcons = {
		control: (
			<svg class="ap-w-4 ap-h-4 ap-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
			</svg>
		),
		style: (
			<svg class="ap-w-4 ap-h-4 ap-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
			</svg>
		),
		advanced: (
			<svg class="ap-w-4 ap-h-4 ap-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
			</svg>
		),
	};

	return (
		<section class={`ap-w-full ap-mt-4 ${local.class || ''}`} {...others}>
			<Show when={showTabs()}>
				<div class="ap-flex ap-w-full ap-border-b-2 ap-border-slate-200 ap-mb-6 ap-gap-1">
					<For each={tabKeys()}>
						{(tabKey) => (
							<button
								onClick={() => setTab(tabKey)}
								class={`ap-relative ap-flex ap-items-center ap-gap-2 ap-px-5 ap-py-3 ap-text-sm ap-font-semibold ap-transition-all ap-duration-200 ap-capitalize ap-rounded-t-md ${
									tab() === tabKey
										? 'ap-text-indigo-600 ap-bg-white ap-border-b-2 ap-border-indigo-600 ap--mb-0.5'
										: 'ap-text-slate-600 hover:ap-text-slate-900 hover:ap-bg-slate-50'
								}`}
							>
								{tabIcons[tabKey]}
								<span>{tabs()[tabKey]}</span>
							</button>
						)}
					</For>
				</div>
			</Show>

			<div
				class="ap-space-y-6"
				classList={{ 'ajaxpress-locked ajaxpress-locked-fit': isLocked() }}
			>
				<Show when={tab() === 'control' && controlContent()}>
					<div>{controlContent()}</div>
				</Show>

				<Show when={tab() === 'style' && styleContent()}>
					<div>{styleContent()}</div>
				</Show>

				<Show when={tab() === 'advanced' && advancedContent()}>
					<div>{advancedContent()}</div>
				</Show>
			</div>
		</section>
	);
}
